'use client';
import { SignInButton, SignUpButton, useAuth } from '@clerk/nextjs';
import { useConfig } from '@marine/configs';
import { Button, Grid, Menu, type MenuProps, Modal, Space } from 'antd';
import { Search } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useOpenPanel } from 'providers';
import { useCallback, useMemo, useState } from 'react';
import styles from 'sailor-ui/styles/Header.module.scss';
import { AccountUserButton } from './UserNav/AccountUserButton';

// Loaded on demand: SearchPanel pulls in Meilisearch and react-instantsearch, and it lives in a
// modal that is closed on first paint, so none of it is needed until the user opens search. It
// renders nothing server-side either way, so deferring it cannot change the HTML crawlers see.
const SearchPanel = dynamic(() => import('@components/search/SearchPanel').then((m) => m.SearchPanel), {
  ssr: false,
});

const RightMenu: React.FC = () => {
  const { isSignedIn } = useAuth();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const openPanel = useOpenPanel();

  const { theboatAppUrl, theboatDbUrl } = useConfig();
  const pathname = usePathname();
  const closeSearchModal = () => setShowSearchModal(false);

  // Signing in must leave the visitor where they were, not bounce them into the app. Sign-in runs
  // on the primary domain, so the return has to be absolute for Clerk to accept it — the origin is
  // already in `allowedRedirectOrigins`. Query strings are deliberately dropped: reading them needs
  // `useSearchParams`, which would force this header (and so every page) out of the static shell.
  const signInReturnUrl = `${theboatDbUrl ?? ''}${pathname}`;

  const trackSignUp = useCallback(async () => {
    openPanel.track('sign_up', { source: 'Header Right Menu' });
  }, [openPanel.track]);

  const { xl } = Grid.useBreakpoint();

  const searchModalTop = useMemo(() => (xl ? 20 : 0), [xl]);

  const menuItems = useMemo((): MenuProps['items'] => {
    const searchItem: MenuProps['items'][number] = {
      key: 'search-menu',
      label: 'Search',
      icon: <Search size={20} />,
      onClick: () => {
        setShowSearchModal(true);
      },
      className: 'searchMenuTrigger',
    };

    // Nothing account-shaped here: `UserDrawerNav`, which `InteractiveHeader` renders beside this
    // menu, carries the Clerk user button and every account link hangs off that.
    if (isSignedIn) {
      return [searchItem];
    }

    return [
      searchItem,
      {
        key: 'login',
        label: (
          <SignInButton forceRedirectUrl={signInReturnUrl} fallbackRedirectUrl={signInReturnUrl}>
            <Button type="link" block className={styles.loginButtonDrawer}>
              Login
            </Button>
          </SignInButton>
        ),
      },
      {
        key: 'signUp',
        label: (
          <SignUpButton forceRedirectUrl={theboatAppUrl} fallbackRedirectUrl={theboatAppUrl}>
            <Button onClick={trackSignUp} className="ant-btn-special">
              Sign up for free
            </Button>
          </SignUpButton>
        ),
      },
    ];
  }, [isSignedIn, trackSignUp, theboatAppUrl, signInReturnUrl]);

  return (
    <>
      {xl ? (
        <div className={styles.rightMenuDesktop}>
          <Space size={'medium'}>
            <Button onClick={() => setShowSearchModal(true)} icon={<Search size={14} />}>
              Search
            </Button>
            {!isSignedIn ? (
              <>
                <SignInButton forceRedirectUrl={signInReturnUrl} fallbackRedirectUrl={signInReturnUrl}>
                  <Button type="link" className={styles.loginButtonDesktop}>
                    Login
                  </Button>
                </SignInButton>
                <SignUpButton forceRedirectUrl={theboatAppUrl} fallbackRedirectUrl={theboatAppUrl}>
                  <Button onClick={trackSignUp} className="ant-btn-special">
                    Sign up for free
                  </Button>
                </SignUpButton>
              </>
            ) : (
              <div>
                <AccountUserButton />
              </div>
            )}
          </Space>
        </div>
      ) : (
        <Menu mode="inline" selectable={false} className="rightMenu" items={menuItems} />
      )}
      <Modal
        className={styles.searchModal}
        footer={null}
        style={{ top: searchModalTop }}
        width={1200}
        open={showSearchModal}
        onCancel={closeSearchModal}
      >
        {showSearchModal && <SearchPanel isShown={showSearchModal} closeSearchModal={closeSearchModal} />}
      </Modal>
    </>
  );
};

export default RightMenu;
